import * as React from 'react';
import * as R from 'ramda';
import { BaseRouteProps, BaseFormState } from '../../interfaces';
import { UserStore } from '../../stores';
import { observer, inject } from 'mobx-react';
import { Form, Text, Checkbox, FormValues, FormErrors, FormApi } from 'react-form';
import { UserInfo } from '../../models';
import { ImageUpload } from '..';
import { UserDataService, ValidationService } from '../../services';
import { Panel } from 'react-bootstrap';

interface UserSettingsProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
}

interface UserSettingsState extends BaseFormState {
    originalEntity: UserInfo;
    emailValidating: boolean;
}

@inject('UserStore') @observer
export class UserSettings extends React.Component<UserSettingsProps, UserSettingsState> {
    private vs = new ValidationService();
    private userService = new UserDataService(this.props.notificationService);
    private formApi: FormApi;

    constructor(props: UserSettingsProps) {
        super(props);

        this.state = {
            canSubmit: false,
            formErrors: {},
            originalEntity: props.UserStore!.userInfo,
            emailValidating: false
        };
    }

    handleImageUpload = (file: File) => {
        this.userService.uploadImage(this.state.originalEntity.id, file)
            .then((response) => { this.props.UserStore!.updateAvatar(response); });
    }

    requestPasswordResetEmail = () => {
        const { notificationService } = this.props;
        this.userService.sendPasswordReset(this.state.originalEntity.id)
            .then(() => notificationService.addNotification('Reset password email sent successfully. Please check your email.'))
            .catch(() => notificationService.addError('Error sending password reset email. Please try again.'));
    }

    save = (values: any) => {
        this.userService.updateUserSettings(values).then((response: UserSettings) => {
            this.props.UserStore!.mergeUpdatedUserSettings(response);
        });
    }

    validateErrors = (values: FormValues) => {
        const { originalEntity } = this.state;
        const { firstName, lastName, email, confirmEmail } = values;
        let formErrors: any = {};

        if (firstName || firstName === '') {
            if (!this.vs.required(firstName)) {
                formErrors.firstName = 'First name is required.';
            } else if (!this.vs.cannotBeEmpty(firstName!)) {
                formErrors.firstName = 'First name cannot be blank.';
            }
        }

        if (lastName || lastName === '') {
            if (!this.vs.required(lastName)) {
                formErrors.lastName = 'Last name is required.';
            } else if (!this.vs.cannotBeEmpty(lastName!)) {
                formErrors.lastName = 'Last name cannot be blank.';
            }
        }

        if ((confirmEmail || confirmEmail === '') && email !== originalEntity.email) {
            if (confirmEmail !== email) {
                formErrors.confirmEmail = 'Provided emails do not match';
            }
        }

        const existingEmailError = this.state.formErrors.email;
        if (existingEmailError != null) {
            formErrors.email = existingEmailError;
        }

        return formErrors;
    }

    validateSuccess = (values: FormValues, errors: FormErrors) => {
        const { originalEntity } = this.state;
        const requiredFields = originalEntity.email === values.email ? ['firstName', 'lastName', 'email'] : ['firstName', 'lastName', 'email', 'confirmEmail'];

        // make sure we have no errors and all the required values
        if (R.isEmpty(errors) && this.vs.validateRequiredFieldsExist(requiredFields, values)) {
            this.setState({ canSubmit: true, formErrors: errors });
        } else {
            this.setState({ canSubmit: false, formErrors: errors });
        }

        return errors;
    }

    checkEmailAddress = (e: any) => {
        const email = this.formApi.getFormState().values.email;
        const { originalEntity } = this.state;

        if (!email || email === '') {
            this.setState({ formErrors: Object.assign({ ...this.state.formErrors }, { email: 'Email is required' }) }, () => { this.formApi.setTouched('email', true); });
        } else if (!e.target.validity.valid) {
            this.setState({
                formErrors: Object.assign({ ...this.state.formErrors }, { email: 'Invalid Email format' })
            });
        } else {
            this.setState({ emailValidating: true });

            this.userService.checkIfEmailIsInUse(email, originalEntity.id)
                .then((response: Response) => {
                    response.json().then((emailInUse) => {
                        if (emailInUse) {
                            this.setState(
                                {
                                    emailValidating: false,
                                    formErrors: Object.assign({ ...this.state.formErrors }, { email: 'The provided email is already in use' })
                                },
                                () => { this.formApi.setTouched('email', true); }
                            );
                        } else {
                            this.setState(
                                {
                                    emailValidating: false,
                                    formErrors: Object.assign({ ...this.state.formErrors }, { email: null })
                                },
                                () => { this.formApi.setTouched('email', true); }
                            );
                        }
                    });
                })
                .catch(() => {
                    this.setState({ formErrors: Object.assign({ ...this.state.formErrors }, { email: 'An error occurred while checking email availability' }) }, () => { this.formApi.setTouched('email', true); });
                });
        }
    }

    setFormApi = (api: FormApi) => {
        this.formApi = api;
    }

    render() {
        const { originalEntity, formErrors, canSubmit } = this.state;

        let imageUploadComponent: any = null;
        if (originalEntity.id) {

            let imageUrl;
            if (originalEntity.avatar) {
                imageUrl = `data:image/png;base64,${originalEntity.avatar}`;
            }

            imageUploadComponent = (<ImageUpload maxWidth={150} maxHeight={150} uploadHandler={this.handleImageUpload} imageBase64String={imageUrl} label="Avatar" />);
        }

        return (
            <div>
                <div className="content-header">
                    <div className="content-container">
                        <div className="title">
                            Profile Settings
                        </div>
                    </div>
                </div>

                <div className="content-body">
                    <div className="content-container">
                        <Panel>
                            <Panel.Heading>
                                <Panel.Title componentClass="h3">User Info</Panel.Title>
                            </Panel.Heading>
                            <Panel.Body>
                                <Form
                                    defaultValues={originalEntity}
                                    onSubmit={submittedValues => this.save(submittedValues)}
                                    validateError={this.validateErrors}
                                    validateSuccess={this.validateSuccess}
                                    getApi={this.setFormApi}
                                >
                                    {formApi => (
                                        <form onSubmit={formApi.submitForm} id="entityForm">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="form-group">
                                                        <label htmlFor="firstName">First Name *</label>
                                                        <Text className="form-control" field="firstName" id="firstName" />
                                                        {formErrors.firstName && <span className="help-block error-text">{formErrors.firstName}</span>}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-group">
                                                        <label htmlFor="lastName">Last Name *</label>
                                                        <Text className="form-control" field="lastName" id="lastName" />
                                                        {formErrors.lastName && <span className="help-block error-text">{formErrors.lastName}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="form-group">
                                                        <label htmlFor="email">Email *</label>
                                                        <Text className="form-control" field="email" id="email" onKeyUp={this.checkEmailAddress} />
                                                        {formErrors.email && <span className="help-block error-text">{formErrors.email}</span>}
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <div className="form-group">
                                                        <label htmlFor="confirmEmail">Confirm Email *</label>
                                                        <Text className="form-control" field="confirmEmail" id="confirmEmail" disabled={originalEntity.email === formApi.values.email} />
                                                        {formErrors.confirmEmail && <span className="help-block error-text">{formErrors.confirmEmail}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <div className="form-group">
                                                        <label htmlFor="resultsArePrivate">Keep My Results Private?</label>
                                                        <Checkbox className="checkbox" field="resultsArePrivate" />
                                                    </div>
                                                </div>
                                            </div>

                                            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>Save</button>
                                        </form>
                                    )}
                                </Form>
                            </Panel.Body>
                        </Panel>

                        <Panel>
                            <Panel.Heading>
                                <Panel.Title componentClass="h3">Actions</Panel.Title>
                            </Panel.Heading>
                            <Panel.Body>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group">
                                            <button className="btn btn-primary" onClick={this.requestPasswordResetEmail}>
                                                <span className="glyphicon glyphicon-envelope" /> Request Password Reset Email
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        {imageUploadComponent}
                                    </div>
                                </div>
                            </Panel.Body>
                        </Panel>
                    </div>
                </div>
            </div >);
    }
}


// WEBPACK FOOTER //
// ./src/components/usersettings/UserSettings.tsx